import { Card } from '@/common/ui/card'
import useQueryGetPlayerCumulative from '@/modules/manage-player/api/hooks/useQueryGetPlayerCumulative'
import {
	Wallet,
	ArrowDownToLine,
	ArrowUpFromLine,
	DollarSign,
	Gift,
	Trophy,
	ListFilter,
	Sparkles,
	Calendar,
	RotateCcw,
	PartyPopper,
} from 'lucide-react'
import { Skeleton } from '@/common/ui/skeleton'
import { Button } from '@/common/ui/button'
import { useState } from 'react'
import GameTypeDetailsModal from './components/CumulativeByGameType'
import { GetPlayerDetailResponse } from '@/modules/manage-player/type'

const FIRST_ROW_CARDS = [
	{ icon: ArrowDownToLine, colorClass: 'text-green-500' },
	{ icon: ArrowUpFromLine, colorClass: 'text-red-500' },
	{ icon: Wallet, colorClass: 'text-green-500' },
	{ icon: DollarSign, colorClass: 'text-red-500' },
]

interface StatCardProps {
	title: string
	value: string
	count?: string
	icon: React.ReactNode
	colorClass?: string
	isLoading?: boolean
}

function StatCard({
	title,
	value,
	count,
	icon,
	colorClass = 'text-primary',
	isLoading = false,
}: StatCardProps) {
	return (
		<Card className="relative overflow-hidden transition-all hover:shadow-md">
			<div className="p-4 sm:p-6">
				<div className="flex items-center justify-between">
					<div className="w-full space-y-2">
						<div className={`inline-flex items-center gap-2 ${colorClass}`}>
							<div className="rounded-full bg-primary/10 p-2">
								{isLoading ? (
									<Skeleton className="h-4 w-4 sm:h-5 sm:w-5" />
								) : (
									icon
								)}
							</div>
							{isLoading ? (
								<Skeleton className="h-4 w-20 sm:h-5 sm:w-24" />
							) : (
								<p className="text-xs font-medium sm:text-sm">{title}</p>
							)}
						</div>
						{isLoading ? (
							<div className="space-y-2">
								<Skeleton className="h-6 w-full sm:h-8" />
							</div>
						) : (
							<h3 className="text-lg font-semibold tracking-tight sm:text-2xl">
								{value}
								{count && (
									<span className="ml-2 block text-xs font-normal text-muted-foreground sm:inline sm:text-sm">
										({count})
									</span>
								)}
							</h3>
						)}
					</div>
				</div>
			</div>
		</Card>
	)
}

interface PlayerCumulativeProps {
	playerId: string
	data: GetPlayerDetailResponse
}

export default function PlayerCumulative({
	playerId,
	data,
}: PlayerCumulativeProps) {
	const { data: cumulativeData, isLoading } =
		useQueryGetPlayerCumulative(playerId)
	const [isGameTypeModalOpen, setIsGameTypeModalOpen] = useState(false)

	const renderLoadingCards = () => (
		<>
			<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
				{FIRST_ROW_CARDS.map((card, index) => (
					<StatCard
						key={index}
						title=""
						value=""
						icon={<card.icon className="h-4 w-4 sm:h-5 sm:w-5" />}
						colorClass={card.colorClass}
						isLoading={true}
					/>
				))}
			</div>
		</>
	)

	return (
		<div className="space-y-4 sm:space-y-6">
			{/* Header with responsive button */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<h2 className="text-xl font-bold tracking-tight sm:text-2xl">
						สรุปข้อมูลการเงิน
					</h2>
					<p className="text-sm text-muted-foreground sm:text-base">
						ภาพรวมการทำธุรกรรมและการเล่นทั้งหมด
					</p>
				</div>
				{!isLoading && cumulativeData?.gameTypeTransactionCumulative && (
					<Button
						variant="outline"
						onClick={() => setIsGameTypeModalOpen(true)}
						className="w-full gap-2 sm:w-auto"
						size="sm"
					>
						<ListFilter className="h-4 w-4" />
						<span className="sm:hidden">ดูตามประเภทเกม</span>
						<span className="hidden sm:inline">ดูข้อมูลตามประเภทเกม</span>
					</Button>
				)}
			</div>

			{isLoading ? (
				renderLoadingCards()
			) : (
				<>
					{/* Main Financial Stats - Responsive Grid */}
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						<StatCard
							title="ยอดฝากรวม"
							value={`${cumulativeData?.totalDepositAmount.toFixed(2)} บาท`}
							count={`จำนวน ${cumulativeData?.totalDepositTime.toFixed(0)} ครั้ง`}
							icon={<ArrowDownToLine className="h-4 w-4 sm:h-5 sm:w-5" />}
							colorClass="text-green-500"
						/>
						<StatCard
							title="ยอดถอนรวม"
							value={`${cumulativeData?.totalWithdrawAmount.toFixed(2)} บาท`}
							count={`จำนวน ${cumulativeData?.totalWithdrawTime.toFixed(0)} ครั้ง`}
							icon={<ArrowUpFromLine className="h-4 w-4 sm:h-5 sm:w-5" />}
							colorClass="text-red-500"
						/>
						<StatCard
							title="ยอดเทิร์นรวม"
							value={`${cumulativeData?.totalTurnoverAmount.toFixed(2)} บาท`}
							icon={<Trophy className="h-4 w-4 sm:h-5 sm:w-5" />}
							colorClass="text-yellow-500"
						/>

						<StatCard
							title="โบนัสฝากรวม"
							value={`${cumulativeData?.totalDepositBonusAmount.toFixed(2)} บาท`}
							icon={<Gift className="h-4 w-4 sm:h-5 sm:w-5" />}
							colorClass="text-purple-500"
						/>
					</div>

					{/* Rewards Section */}
					<div>
						<h3 className="mb-3 text-base font-semibold sm:mb-4 sm:text-lg">
							รางวัลและโบนัส
						</h3>
						<div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4">
							<StatCard
								title="รางวัลวงล้อโชค"
								value={`${(data?.totalLuckyWheelRewards || 0).toFixed(2)} บาท`}
								icon={<Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />}
								colorClass="text-pink-500"
							/>
							<StatCard
								title="รางวัลเช็คอินรายวัน"
								value={`${(data?.totalDailyCheckInRewards || 0).toFixed(2)} บาท`}
								icon={<Calendar className="h-4 w-4 sm:h-5 sm:w-5" />}
								colorClass="text-blue-500"
							/>
							<StatCard
								title="รางวัลคืนทุน"
								value={`${(data?.totalReturnLossRewards || 0).toFixed(2)} บาท`}
								icon={<RotateCcw className="h-4 w-4 sm:h-5 sm:w-5" />}
								colorClass="text-orange-500"
							/>
							<StatCard
								title="รางวัลอีเวนต์รวม"
								value={`${(data?.totalEventRewards || 0).toFixed(2)} บาท`}
								icon={<PartyPopper className="h-4 w-4 sm:h-5 sm:w-5" />}
								colorClass="text-indigo-500"
							/>
						</div>
					</div>

					<GameTypeDetailsModal
						isOpen={isGameTypeModalOpen}
						onClose={() => setIsGameTypeModalOpen(false)}
						gameTypeData={cumulativeData?.gameTypeTransactionCumulative || []}
					/>
				</>
			)}
		</div>
	)
}
